import React, { forwardRef, ReactElement, useEffect, useImperativeHandle, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';

import { dusk } from '../../utils/helpers';

type Props = {
    readonly buttonContent: string | JSX.Element;
    readonly buttonTitle?: string;
    readonly buttonClass?: string;
    readonly modalTitle: string;
    readonly modalContent: string | ReactElement;
    readonly onModalOpen?: Function;
    readonly onModalClose?: Function;
};

export type ModalPopupInterface = { closeModal: Function; openModal: Function };

function ModalPopup(
    {
        buttonContent,
        buttonTitle = '',
        buttonClass = null,
        modalTitle,
        modalContent,
        onModalOpen = null,
        onModalClose = null,
    }: Props,
    ref: React.Ref<ModalPopupInterface>
): ReactElement | null {
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (showModal) {
            callCallback(onModalOpen);
        } else {
            callCallback(onModalClose);
        }
    }, [showModal]);

    function callCallback(callback?: Function) {
        if (callback) {
            callback();
        }
    }

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleShowModal = () => {
        setShowModal(true);
    };

    useImperativeHandle(ref, () => ({
        closeModal() {
            handleCloseModal();
        },
        openModal() {
            handleShowModal();
        },
    }));

    return (
        <>
            <button
                {...dusk('openModalBtn')}
                className={`btn ${buttonClass}`}
                type="button"
                onClick={handleShowModal}
                title={buttonTitle}
            >
                {buttonContent}
            </button>
            <Modal show={showModal}>
                <div className="modal-header">
                    <h5 className="modal-title">{modalTitle}</h5>
                    <button
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        aria-label="Close"
                        onClick={handleCloseModal}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div className="modal-body">{modalContent}</div>
            </Modal>
        </>
    );
}

export default forwardRef(ModalPopup);
